import { reactive } from 'vue';
import { PERMISSION_ACTION } from '@/typings/enums/PERMISSION_ACTION';
import { SETTING_TYPE } from '@/typings/enums/SETTING_TYPE';
import { USER_STATUS } from '@/typings/enums/USER_STATUS';
import { useLoader, useNotif } from '@orion.ui/orion';
import { Sentry } from '@/app';
import UserEntity from './UserEntity';
import router from '@/router/index.router';
import SignalrAppInstance from '@/signalr/App.signalr';
import useSelfService from '@/services/SelfService';
import useDiscussionService from '@/services/DiscussionService';
import useUserSettingsService from '@/services/user/UserSettingsService';
import useOrganizationService from '@/services/OrganizationService';
import useRolePermissionsService from '@/services/RolePermissionService';
import useUserService from '@/services/user/UserService';
import { TITLE } from '@/typings/enums/TITLE';
import { SUITE_URL } from '@/utils/CONST';

const SessionManagerImport = () => import('@/utils/SessionManager');

export default class SelfEntity extends UserEntity {
	static async getUserInfosAsync () {
		const user = await useUserService().getUserInfosAsync();
		const selfEntity = new SelfEntity(user);
		const self = useSelfService().setSelfEntity(selfEntity);

		if (!user) return;

		if (user.status === USER_STATUS.HasToConfirmedEmail) {
			router.push({
				name: 'Signup',
				query: { status: 1 },
			});
		} else {
			await SignalrAppInstance.tryStart();

			if (!self.isTempWorker()) {
				const organizations = await useOrganizationService().listAsync();
				self.setOrganizations(organizations);

				await self.getRolePermissionsSelfAsync();
				await self.getUserSettingAsync(SETTING_TYPE.ShowFlex);
			}

			useDiscussionService(self);
		}

		Sentry.configureScope((scope) => {
			scope.setUser({
				id: self.id,
				email: self.authLogin,
				username: self.name,
			});
			scope.setTag('userType', self.readableUserType);
		});

		return user;
	}

	private permissionSelf: IRolePermissionSelfModel[] = [];
	private userSettings = {} as Record<number, any>;
	private startupDataLoaded = false;
	private readonly privateState = reactive({
		organizations: undefined as Undef<IOrganizationSimpleModel[]>,
		organizationUserId: undefined as Undef<Number>,
	});

	get isConnected () { return !!this.id && this.startupDataLoaded; }
	get permissions () { return this.permissionSelf; }
	get organization (): Undef<IOrganizationSimpleModel> { return this.privateState.organizations?.first(); }
	get hasOrganization () { return !!this.organization?.id; };
	get hasToConfirmEmail () { return this.status === USER_STATUS.HasToConfirmedEmail; }
	get organizationUserId () { return this.state.organizationUserId;}
	get suiteUserId () { return this.state.suiteUserId;}

	get avatarUrl () {
		return this.state.suiteUserId
			? `${SUITE_URL}/app/user/${this.suiteUserId}/avatar`
			: this.avatarId;
	}

	get isMale () { return this.state.personalInformationTitle !== TITLE.Madam; } // peut être undefined

	isFullyLoaded (val = true) {
		this.startupDataLoaded = val;
	}

	async getRolePermissionsSelfAsync () {
		this.permissionSelf = await useRolePermissionsService().selfAsync();
	}

	can (key: keyof typeof PERMISSION_ACTION) {
		const permissionActionEnumValue = PERMISSION_ACTION[key];
		return !!this.permissions.find(x => +x.action === permissionActionEnumValue)?.value;
	}

	canOneOf (...keys: (keyof typeof PERMISSION_ACTION)[]) {
		const permissionActionEnumValues = keys.map(key => PERMISSION_ACTION[key]);
		return !!this.permissions
			.filter(x => permissionActionEnumValues.includes(x.action as unknown as typeof permissionActionEnumValues[number]) && x.value).length;
	}

	canAllOf (...keys: (keyof typeof PERMISSION_ACTION)[]) {
		const permissionActionEnumValues = keys.map(key => PERMISSION_ACTION[key]);
		return this.permissions
			.filter(x => permissionActionEnumValues.includes(x.action as unknown as typeof permissionActionEnumValues[number]) && x.value)
			.length === keys.length;
	}

	setRoles (payload: IRoleUserModel[]) {
		this.state.roleUsers = payload;
	}

	setOrganizations (payload: IOrganizationSimpleModel[]) {
		this.privateState.organizations = payload;
	}

	async getUserSettingAsync (settingType: SETTING_TYPE) {
		if (!(settingType in this.userSettings)) {
			const data = await useUserSettingsService().selectSettingAsync(settingType);
			this.userSettings[settingType] = data?.value;
		}
		return this.userSettings[settingType];
	}

	async updateUserSettingAsync ({ settingType, value }: { settingType: SETTING_TYPE, value: any }) {
		await useUserSettingsService().updateSettingAsync({
			settingType,
			value,
		});
		this.userSettings[+settingType] = value;
	}

	async logoutAsync () {
		const SessionManager = await SessionManagerImport();
		await SessionManager.default.logoutAsync();
	}

	async refreshAsync () {
		const data = await useUserService().getUserInfosAsync();
		if (!!data) this.assignData(data);
	}

	organizationGuard (action: 'notif' | 'redirect' | 'none' = 'notif'): this is (SelfEntity & { organization: IOrganizationSimpleModel }) {
		if (this.organization?.id) {
			return true;
		} else {
			if (action === 'notif') useNotif.warning(`Vous ne faites actuellement partie d'aucune organisation`);
			if (action === 'redirect') router.replace({ name: 'OrganizationDashboard' });
			return false;
		}
	}
}
